// web/src/shell/AppShell.tsx — the enterprise app shell (M0.4). Composes the
// persistent left nav, the top bar, the breadcrumb trail, and the routed page
// outlet. The nav collapses on narrow viewports and via the collapse control;
// the state persists. Shell-level state (principal, theme, act-as, realtime) is
// owned here and published through ShellContext. Token-only; dark-first.
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { Breadcrumbs } from './Breadcrumbs';
import { ShellProvider, type ShellState } from './ShellContext';

const NAV_KEY = 'bg-nav-collapsed';

export function AppShell({
  shell,
  outletContext,
}: {
  shell: ShellState;
  /** Forwarded to the routed <Outlet> so nested pages can read shell-owned data. */
  outletContext?: unknown;
}): React.JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(NAV_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Auto-collapse on narrow viewports; user toggle still wins thereafter.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const apply = (): void => {
      if (mq.matches) setCollapsed(true);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  function toggle(): void {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(NAV_KEY, next ? '1' : '0');
      } catch {
        /* ignore storage failures */
      }
      return next;
    });
  }

  return (
    <ShellProvider value={shell}>
      <div className={`bgAppShell ${collapsed ? 'bgAppShellCollapsed' : ''}`}>
        <SideNav collapsed={collapsed} onToggle={toggle} />
        <div className="bgMainCol">
          <TopBar />
          <div className="bgCrumbBar">
            <Breadcrumbs />
          </div>
          <main className="bgRouteOutlet" id="main-content">
            <Outlet context={outletContext} />
          </main>
        </div>
      </div>
    </ShellProvider>
  );
}

export default AppShell;
